import React from "react";
import { DatePicker } from "@pd/pd-ui-kit";
import type { Dayjs } from "dayjs";
import { Controller } from "react-hook-form";
import type { Control, FieldErrors, FieldValues } from "react-hook-form";

import { Field } from "~/shared/ui/Field";

import styles from "~/pages/ListOfDocuments/Modals/style.less";

interface OptionIProps {
  label: string;
  value: string | number;
}

interface SelectedFieldIProps {
  field: string;
  name: string;
  type?: string;
  control: Control<FieldValues>;
  title: string;
  position?: string | null;
  readOnly?: boolean;
  errors: FieldErrors;
  requisites: OptionIProps[];
  templates: OptionIProps[];
  dataOptions: OptionIProps[];
}

interface DateFieldIProps {
  name: string;
  control: Control<FieldValues>;
  title: string;
  position?: string | null;
  readOnly?: boolean;
  error?: string;
}

// Значение в форме храним как Date (или "" из defaultValues) — этот формат
// ждут мапперы в Modals/helper.ts и dateFormatUTC. onChange кита отдаёт Dayjs,
// поэтому конвертируем обратно в Date.
const DateField = ({
  name,
  control,
  title,
  position,
  readOnly,
  error,
}: DateFieldIProps) => {
  return (
    <Controller
      control={control}
      name={name}
      render={({ value, onChange, onBlur, ref }) => (
        <div className={styles.dataStyleItem}>
          <span className={styles.titleField}>{title}</span>
          <DatePicker
            ref={ref}
            value={value instanceof Date ? value : null}
            onChange={(date: Dayjs | null) =>
              onChange(date ? date.toDate() : null)
            }
            onBlur={onBlur}
            format="DD.MM.YYYY"
            placement={position === "top" ? "topLeft" : "bottomLeft"}
            disabled={readOnly}
            allowClear={false}
            status={error ? "error" : undefined}
            style={{ width: "100%" }}
          />
          {error && <span className={styles.fieldError}>{error}</span>}
        </div>
      )}
    />
  );
};

const SelectedField = ({
  field,
  name,
  type,
  control,
  title,
  position,
  readOnly,
  errors,
  requisites,
  templates,
  dataOptions,
}: SelectedFieldIProps) => {
  // Скрытые поля (vaz_requisitesId, parentContractId) не рендерим —
  // их значения живут в state формы и выставляются через setValue.
  if (type === "hidden") {
    return null;
  }

  switch (field) {
    case "datepicker":
      return (
        <DateField
          name={name}
          control={control}
          title={title}
          position={position}
          readOnly={readOnly}
          error={errors[name]?.message}
        />
      );
    case "select": {
      const options =
        name === "printFormId"
          ? templates
          : name === "avtovazAccount"
            ? requisites
            : dataOptions;
      return (
        <Field
          type="select"
          name={name}
          control={control}
          label={title}
          options={options}
          labelInValue
          disabled={readOnly}
          error={errors[name]?.message}
          wrapperClassName={styles.dataStyleItem}
        />
      );
    }
    case "input":
    default:
      return (
        <Field
          type="input"
          name={name}
          control={control}
          label={title}
          disabled={readOnly}
          error={errors[name]?.message}
          wrapperClassName={styles.dataStyleItem}
        />
      );
  }
};

export default SelectedField;
